import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import I18n from 'i18n-js'

import { useSegmentsStore } from '@app/insights/store/segments'
import * as api from './api'
import { segmentKey, runKey } from './segment-key'
import {
  matchesSearch,
  matchesVisibility,
  matchesPublisher,
  sortSegments,
} from './utils'
import { allClientsQuery, isAllClientsQuery } from './all-clients-query'
import type { SegmentErrorResponse } from '@app/insights/types'
import {
  publisherOptions,
  visibilityOptions,
  sortOptions,
  segmentGroupKeys,
} from './constants'
import type {
  PendingQueryRun,
  PendingRunsSubscription,
  PinnedSegment,
  QueryRow,
  RecentSegment,
  Segment,
  VisibilityFilter,
  PublisherFilter,
  SortOption,
  SegmentGroup,
} from './types'
const RECENTLY_VISITED_LIMIT = 3

type InitPayload = {
  recentSegments: RecentSegment[]
  pinnedSegments: PinnedSegment[]
  newlyAddedPartnerSegmentIds: number[]
  pendingQueryRuns: PendingQueryRun[]
  pendingRunsSubscription: PendingRunsSubscription | null
}

export const useSegmentsOverviewStore = defineStore('segments-overview', () => {
  // The list of segments lives in useSegmentsStore — the same store the
  // EditSegmentModal mutates on save and the global deleteSegment removes
  // from. Reading from it here means edits/deletes propagate to this page
  // automatically.
  const segmentsStore = useSegmentsStore()

  // Starts true: the queries are fetched on mount, and until they arrive the
  // column would show the "All clients" row alone, as if that were all of them.
  const isLoading = ref(true)
  const loadError = ref(false)
  const searchValue = ref('')
  const visibilityFilter = ref<VisibilityFilter>(visibilityOptions.ANY)
  const publisherFilter = ref<PublisherFilter>(publisherOptions.ALL)
  const sortOption = ref<SortOption>('default')

  // Keys of segments with a pin/unpin request in flight. Used to make the pin
  // icon inert so a double-click can't fire pin-then-unpin back-to-back.
  const pendingPinKeys = ref<Set<string>>(new Set())
  const revealedRowKey = ref<string | null>(null)

  const recentSegments = ref<RecentSegment[]>([])
  const pinnedSegments = ref<PinnedSegment[]>([])
  const newlyAddedPartnerSegmentIds = ref<number[]>([])

  // Seeded by the server; only ever shrinks, since nothing on this page starts
  // a run — opening a query is a full navigation.
  const pendingQueryRuns = ref<PendingQueryRun[]>([])
  const pendingRunsSubscription = ref<PendingRunsSubscription | null>(null)

  const segments = computed<Segment[]>(() => {
    return segmentsStore.segments || []
  })

  function init(payload: InitPayload): void {
    recentSegments.value = payload.recentSegments
    pinnedSegments.value = payload.pinnedSegments
    newlyAddedPartnerSegmentIds.value = payload.newlyAddedPartnerSegmentIds
    pendingQueryRuns.value = payload.pendingQueryRuns
    pendingRunsSubscription.value = payload.pendingRunsSubscription
  }

  function seedPins(pins: PinnedSegment[]): void {
    pinnedSegments.value = pins
  }

  async function fetchAll(): Promise<void> {
    isLoading.value = true
    loadError.value = false
    try {
      await segmentsStore.fetchSegments()
    } catch {
      // Surface the failure instead of letting it look like an empty firm.
      loadError.value = true
    } finally {
      isLoading.value = false
    }
  }

  function setSearch(value: string): void {
    searchValue.value = value
  }

  function filterByVisibility(value: VisibilityFilter): void {
    visibilityFilter.value = value
  }

  function filterByPublisher(value: PublisherFilter): void {
    publisherFilter.value = value
  }

  function sortBy(value: SortOption): void {
    sortOption.value = value
    revealedRowKey.value = null
  }

  // Normalize the search term once per change instead of per segment per render.
  const searchTerm = computed(() => searchValue.value.trim().toLowerCase())

  const filteredSegments = computed((): QueryRow[] => {
    const rows: QueryRow[] = [...segments.value, allClientsQuery()]
    const filtered = rows.filter(
      (s) =>
        matchesSearch(s, searchTerm.value) &&
        matchesPublisher(s, publisherFilter.value) &&
        matchesVisibility(s, visibilityFilter.value)
    )
    const sorted = sortSegments(filtered, sortOption.value)
    if (sortOption.value !== sortOptions.DEFAULT) return sorted

    const allClients = sorted.filter(isAllClientsQuery)
    return [...allClients, ...sorted.filter((s) => !isAllClientsQuery(s))]
  })

  // Sort reorders rather than narrows the list, so it doesn't count as a
  // filter for the purpose of hiding the side panel.
  const hasActiveFilters = computed(
    (): boolean =>
      searchTerm.value !== '' ||
      publisherFilter.value !== publisherOptions.ALL ||
      visibilityFilter.value !== visibilityOptions.ANY
  )

  // A computed whose value is itself a function ("parameterized getter"):
  // Pinia can only tell a setup store's actions and getters apart by whether
  // the returned value is a plain function or a ref/computed. A plain
  // function taking an argument is indistinguishable from an action, so
  // tooling that stubs actions (e.g. createTestingPinia) would stub this too.
  // Wrapping it in computed() marks it unambiguously as a getter.
  const isPinned = computed(() => (segment: QueryRow): boolean => {
    return pinOrder.value.has(segmentKey(segment))
  })

  const pinOrder = computed(
    (): Map<string, number> =>
      new Map(pinnedSegments.value.map((pin, idx) => [runKey(pin), idx]))
  )

  const segmentGroups = computed((): SegmentGroup[] => {
    if (sortOption.value !== sortOptions.DEFAULT) return []

    const order = pinOrder.value
    const pinned: QueryRow[] = []
    const recommended: QueryRow[] = []
    const custom: QueryRow[] = []
    filteredSegments.value.forEach((row) => {
      if (isAllClientsQuery(row) || order.has(segmentKey(row))) pinned.push(row)
      else if (row.recommended) recommended.push(row)
      else custom.push(row)
    })
    pinned.sort(
      (a, b) =>
        (order.get(segmentKey(a)) ?? -1) - (order.get(segmentKey(b)) ?? -1)
    )

    const groups: SegmentGroup[] = [
      { key: segmentGroupKeys.PINNED, rows: pinned },
      { key: segmentGroupKeys.RECOMMENDED, rows: recommended },
      { key: segmentGroupKeys.CUSTOM, rows: custom },
    ]
    return groups.filter((group) => group.rows.length > 0)
  })

  const newlyAddedSegments = computed((): Segment[] => {
    const ids = new Set(newlyAddedPartnerSegmentIds.value)
    return segments.value.filter(
      (s) => s.partner_segment_id && ids.has(s.partner_segment_id)
    )
  })

  // Resolve each recent reference (backend order, newest-first) to its full
  // segment and keep the most recent few. Recents whose segment isn't in the
  // list (e.g. no longer visible) drop out.
  const recentlyVisitedSegments = computed((): Segment[] => {
    const byKey = new Map(segments.value.map((s) => [segmentKey(s), s]))
    return recentSegments.value
      .map((recent) => byKey.get(runKey(recent)))
      .filter((s): s is Segment => Boolean(s))
      .slice(0, RECENTLY_VISITED_LIMIT)
  })

  const isPinPending = computed(
    () =>
      (segment: QueryRow): boolean =>
        pendingPinKeys.value.has(segmentKey(segment))
  )

  const pendingRunKeys = computed(
    (): Set<string> => new Set(pendingQueryRuns.value.map(runKey))
  )

  // Wrapped in computed() for the same reason as isPinned above.
  const isRunPending = computed(
    () =>
      (segment: QueryRow): boolean =>
        pendingRunKeys.value.has(segmentKey(segment))
  )

  // Keyed by request_ref: the same query can be re-run under a new one.
  function markRunEnded(requestRef: string): void {
    pendingQueryRuns.value = pendingQueryRuns.value.filter(
      (run) => run.request_ref !== requestRef
    )
  }

  async function togglePin(segment: QueryRow): Promise<void> {
    const key = segmentKey(segment)
    // Ignore re-entrant toggles while a request for this segment is in flight,
    // otherwise a double-click would fire pin then unpin (or vice-versa) and
    // the final state would depend on response ordering.
    if (pendingPinKeys.value.has(key)) return

    const target = {
      segmentId: segment.partner_segment_id ? null : segment.id,
      partnerSegmentId: segment.partner_segment_id ?? null,
    }
    const pinned = isPinned.value(segment)
    pendingPinKeys.value = new Set(pendingPinKeys.value).add(key)
    try {
      const response = pinned
        ? await api.unpinSegment(target)
        : await api.pinSegment(target)
      // The server returns the full pin set, so we only adopt it on success.
      // A failed request leaves the local state untouched (consistent with
      // the server) rather than wiping it.
      pinnedSegments.value = response.pinned_segments
      if (!pinned && segmentGroups.value.length > 0) revealedRowKey.value = key
    } catch (e) {
      const message = (e as SegmentErrorResponse).body.errorMessage
      window.SilverfinFlash.addFlash(
        'error',
        message || I18n.t('companies_overview.segments.pin_error')
      )
    } finally {
      const next = new Set(pendingPinKeys.value)
      next.delete(key)
      pendingPinKeys.value = next
    }
  }

  function clearRevealedRow(): void {
    revealedRowKey.value = null
  }

  // Key of the segment a "Make a copy" was started from, while its save-as
  // modal is open — lets the overview app tell the created copy (different
  // key) from a cancelled save-as (source still loaded, same key).
  const copySourceKey = ref<string | null>(null)
  // Guards makeCopy against overlapping calls: a copy started while another
  // one's loadSegment is still in flight supersedes it, and the stale call
  // must not open the modal, clear the newer copy's source key or flash.
  let copyAttempt = 0

  async function makeCopy(segment: Segment): Promise<void> {
    const attempt = ++copyAttempt
    copySourceKey.value = segmentKey(segment)
    // createSegment (the save-as modal's save) serializes the *active*
    // filters, which only loadSegment populates on this page — a copy
    // without this load would be created filterless.
    const loaded = await segmentsStore.loadSegment(
      { id: segment.id, partner_segment_id: segment.partner_segment_id },
      { redirectOnEmpty: false }
    )
    if (attempt !== copyAttempt) return
    // The card menus stay clickable while the load is in flight; if the user
    // opened another modal meanwhile (e.g. edit on a different query), that
    // action supersedes the copy — abort silently rather than hijack it.
    if (segmentsStore.segmentModalType !== null) {
      copySourceKey.value = null
      return
    }
    if (!loaded) {
      copySourceKey.value = null
      window.SilverfinFlash.addFlash(
        'error',
        I18n.t('companies_overview.segments.load_error')
      )
      return
    }
    segmentsStore.openSaveAsSegmentModal()
  }

  function clearCopySource(): void {
    copySourceKey.value = null
  }

  return {
    segments,
    isLoading,
    loadError,
    searchValue,
    visibilityFilter,
    publisherFilter,
    sortOption,
    recentSegments,
    pinnedSegments,
    newlyAddedPartnerSegmentIds,
    pendingQueryRuns,
    pendingRunsSubscription,
    filteredSegments,
    hasActiveFilters,
    segmentGroups,
    newlyAddedSegments,
    recentlyVisitedSegments,
    init,
    seedPins,
    fetchAll,
    setSearch,
    filterByVisibility,
    filterByPublisher,
    sortBy,
    isPinned,
    isPinPending,
    isRunPending,
    markRunEnded,
    togglePin,
    revealedRowKey,
    clearRevealedRow,
    copySourceKey,
    makeCopy,
    clearCopySource,
  }
})
